import { useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  FormField,
  FormItem,
  FormMessage
} from '@@/ui/primitives';
import { useListEntities, useGetEntity } from '../../../hooks/use-entities';
import { useListItems } from '../../../hooks/use-items';
import { AlertTriangleIcon, CheckCheckIcon, ChevronLeft, ChevronRightIcon, Loader2 } from 'lucide-react';
import { cn } from '@@/lib/utils';
import { Spinner } from '@@/ui/components';

const ROOT_ENTITY_ID = 'root';

export function ReferenceConstraints() {
  const form = useFormContext();
  const watchedDefinition = form.watch('definition');
  const selectedEntityId = watchedDefinition.entityId || '';

  const [currentEntityId, setCurrentEntityId] = useState(ROOT_ENTITY_ID);
  const [navigationStack, setNavigationStack] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const { data: currentEntity } = useGetEntity(currentEntityId, { enabled: currentEntityId !== ROOT_ENTITY_ID });
  const { data: entities, isLoading: isLoadingEntities } = useListEntities({ parentId: currentEntityId });
  const { data: items, isLoading: isLoadingItems } = useListItems(currentEntityId, {
    enabled: currentEntityId !== ROOT_ENTITY_ID
  });

  const { data: selectedEntity, isLoading: isSelectedEntityLoading } = useGetEntity(selectedEntityId, {
    enabled: !!selectedEntityId
  });

  const isLoading = isLoadingEntities || isLoadingItems;
  const isAtRoot = currentEntityId === ROOT_ENTITY_ID;

  const filteredEntities = useMemo(() => {
    if (!entities) return [];
    if (!search) return entities;
    return entities.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
  }, [entities, search]);

  const previewItems = useMemo(() => {
    if (!items) return [];
    return items.slice(0, 4);
  }, [items]);

  const handleNavigateToEntity = (entityId: string) => {
    setNavigationStack((prev) => [...prev, currentEntityId]);
    setCurrentEntityId(entityId);
    setSearch('');
  };

  const handleNavigateBack = () => {
    const prevEntityId = navigationStack[navigationStack.length - 1];
    if (prevEntityId) {
      setNavigationStack((prev) => prev.slice(0, -1));
      setCurrentEntityId(prevEntityId);
      setSearch('');
    }
  };

  const handleSelectEntity = (entityId: string) => {
    form.setValue('definition.entityId', entityId);
  };

  return (
    <div className='flex flex-col gap-6 p-4'>
      <div className='flex flex-col gap-1'>
        <h4 className='text-sm font-semibold text-foreground'>Reference</h4>
        <p className='text-xs text-muted-foreground max-w-lg'>
          Select the base Entity for Referencing. Any item under the selected Entity (or its children) can be
          referenced.
        </p>
      </div>
      <div className='flex flex-col gap-4 overflow-hidden'>
        <FormField
          control={form.control}
          name='definition.entityId'
          render={({}) => (
            <FormItem className='flex flex-col gap-2'>
              <div
                className={cn(
                  'rounded-md px-3 py-2 font-medium text-sm flex flex-col gap-1 border',

                  'text-primary bg-muted'
                )}
              >
                <span className='text-xs text-muted-foreground'>Base Entity</span>

                {!isSelectedEntityLoading && !selectedEntity && (
                  <div className='flex items-center gap-2 text-red-800'>
                    <AlertTriangleIcon className='size-4' />

                    <span className='leading-snug'>Entity not found</span>
                  </div>
                )}

                {isSelectedEntityLoading && <Spinner size='sm' />}

                {selectedEntity?.name}

                {!selectedEntityId && (
                  <div className='flex items-center gap-2 text-red-800'>
                    <AlertTriangleIcon className='size-4' />

                    <span className='leading-snug'>None</span>
                  </div>
                )}
              </div>
              <FormMessage className='text-sm text-red-600' />
            </FormItem>
          )}
        />
        <div className='border rounded-md '>
          <Command shouldFilter={false} className='bg-white'>
            <CommandInput
              onValueChange={setSearch}
              className='text-sm!'
              placeholder='Search entities...'
              value={search}
            />
            <CommandList className='max-h-64'>
              {!isAtRoot && (
                <div className='flex items-center border-b px-1 py-1'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={handleNavigateBack}
                    className='h-6 px-1.5! text-sm! leading-snug font-[450]! text-muted-foreground '
                  >
                    <ChevronLeft className='size-4' />
                    {currentEntity && <span className='truncate'>{currentEntity.name}</span>}
                  </Button>
                </div>
              )}
              {isLoading ? (
                <div className='flex items-center justify-center p-4'>
                  <Loader2 className='size-4 animate-spin' />
                  <span className='ml-2 text-muted-foreground text-sm'>Loading...</span>
                </div>
              ) : (
                <>
                  {filteredEntities.length > 0 && (
                    <CommandGroup heading='Entities'>
                      {filteredEntities.map((entity) => {
                        const isSelected = selectedEntityId === entity.entityId;
                        return (
                          <CommandItem
                            key={entity.entityId}
                            value={entity.name}
                            className='flex items-center justify-between'
                          >
                            <button
                              type='button'
                              className='flex-1 text-left py-0.5 flex items-center'
                              onClick={() => handleSelectEntity(entity.entityId)}
                            >
                              <span className='font-medium'>{entity.name}</span>
                              {isSelected && <CheckCheckIcon className='size-4 ml-2 text-primary' />}
                            </button>
                            {entity.childCount > 0 && (
                              <Button
                                type='button'
                                variant='ghost'
                                size='sm'
                                className='h-6 px-1.5!'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavigateToEntity(entity.entityId);
                                }}
                              >
                                <ChevronRightIcon className='size-4' />
                              </Button>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                  {filteredEntities.length === 0 && (
                    <div className='p-4 text-center text-sm text-muted-foreground'>No entities found</div>
                  )}
                </>
              )}
            </CommandList>
          </Command>

          {!isAtRoot && previewItems.length > 0 && (
            <div className='border-t p-3 bg-muted'>
              <div className='text-xs font-medium text-muted-foreground mb-2'>Preview</div>
              <div className='space-y-1'>
                {previewItems.map((item) => (
                  <div key={item.itemId} className='flex items-center gap-2 text-sm text-primary'>
                    <span className='truncate'>{item.primaryKey}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
